import { Plus, Search, X } from 'lucide-react'
import type { CompanyType, MarketType, ProductType } from '@/lib/api/client'
import {
  COMPANY_TYPE_LABELS,
  COMPANY_TYPE_OPTIONS,
  MARKET_LABELS,
  MARKET_OPTIONS,
  PRODUCT_TYPE_LABELS,
  PRODUCT_TYPE_OPTIONS,
} from './portfolioConstants'

export type GroupBy = 'none' | 'companyType' | 'market' | 'productType'

export interface PortfolioFilters {
  search: string
  companyType: CompanyType | null
  market: MarketType | null
  productType: ProductType | null
}

export const EMPTY_FILTERS: PortfolioFilters = {
  search: '',
  companyType: null,
  market: null,
  productType: null,
}

interface PortfolioToolbarProps {
  filters: PortfolioFilters
  onFiltersChange: (next: PortfolioFilters) => void
  groupBy: GroupBy
  onGroupByChange: (next: GroupBy) => void
  onCreate: () => void
}

const filterClass =
  'h-9 px-3 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:text-text hover:border-border-strong focus:outline-none focus:border-accent-500 appearance-none cursor-pointer pr-7'

export function PortfolioToolbar({
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  onCreate,
}: PortfolioToolbarProps) {
  const set = <K extends keyof PortfolioFilters>(key: K, value: PortfolioFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value })

  const tabs: Array<{ key: GroupBy; label: string }> = [
    { key: 'none', label: 'All' },
    { key: 'companyType', label: 'By stage' },
    { key: 'market', label: 'By market' },
    { key: 'productType', label: 'By product type' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => set('search', e.target.value)}
            placeholder="Search projects"
            className="w-full h-9 pl-9 pr-9 bg-surface border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
          />
          {filters.search && (
            <button
              onClick={() => set('search', '')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <FilterSelect
          value={filters.companyType ?? ''}
          onChange={(v) => set('companyType', (v || null) as CompanyType | null)}
          placeholder="Stage"
          options={COMPANY_TYPE_OPTIONS.map((v) => ({ value: v, label: COMPANY_TYPE_LABELS[v] }))}
        />
        <FilterSelect
          value={filters.market ?? ''}
          onChange={(v) => set('market', (v || null) as MarketType | null)}
          placeholder="Market"
          options={MARKET_OPTIONS.map((v) => ({ value: v, label: MARKET_LABELS[v] }))}
        />
        <FilterSelect
          value={filters.productType ?? ''}
          onChange={(v) => set('productType', (v || null) as ProductType | null)}
          placeholder="Product"
          options={PRODUCT_TYPE_OPTIONS.map((v) => ({ value: v, label: PRODUCT_TYPE_LABELS[v] }))}
        />

        <button
          onClick={onCreate}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 bg-accent-500 text-white text-sm font-medium rounded-lg hover:bg-accent-600 active:bg-accent-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New project
        </button>
      </div>

      <div className="flex border-b border-border-subtle">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onGroupByChange(tab.key)}
            className={`py-2.5 mr-6 text-sm font-medium transition-colors -mb-px border-b-2 ${
              groupBy === tab.key
                ? 'text-text border-text'
                : 'text-text-muted border-transparent hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface FilterSelectProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  options: Array<{ value: string; label: string }>
}

function FilterSelect({ value, onChange, placeholder, options }: FilterSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={filterClass}
      >
        <option value="">{placeholder} (any)</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted text-xs pointer-events-none">
        ▾
      </span>
    </div>
  )
}
